import { FunctionComponent, useState } from "react";
import { t } from "i18next";

import { TooltipWrapper } from "../../../components/Shared/Components/TooltipWrapper/TooltipWrapper";
import { translationKeys } from "../../../models/app-translation-keys";

import "./ImageQualityControl.scss";

import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { classNames } from "primereact/utils";

import { ImageQuality } from "../../../../@api";

import { useStore } from "../../../stores/StoreContext";

export interface IChangeImageQualityPayload {
  imageQuality: ImageQuality;
}

interface IImageQualityControlProps {
  imageQuality: ImageQuality;
  disabled: boolean;
  onChange: (imageQuality: ImageQuality) => void;
}

const featureTranslationKeys = translationKeys.feature.imageGeneration;
const imageQualityControlTranslationKeys = featureTranslationKeys.imageQualityControl;

export const ImageQualityControl: FunctionComponent<IImageQualityControlProps> = ({
  imageQuality,
  disabled,
  onChange,
}) => {
  const [isImageQualityValid, setIsImageQualityValid] = useState(true);

  const { globalErrorsStore } = useStore();

  const imageQualityOptions = [
    {
      value: ImageQuality.Auto,
      label: t(imageQualityControlTranslationKeys.imageQualityAutoLabel),
    },
    {
      value: ImageQuality.Low,
      label: t(imageQualityControlTranslationKeys.imageQualityLowLabel),
    },
    {
      value: ImageQuality.Medium,
      label: t(imageQualityControlTranslationKeys.imageQualityMediumLabel),
    },
    {
      value: ImageQuality.High,
      label: t(imageQualityControlTranslationKeys.imageQualityHighLabel),
    },
  ];

  const handleImageQualityChange = (dropdownChangeEvent: DropdownChangeEvent) => {
    const valid = Object.values(ImageQuality).includes(dropdownChangeEvent.value);

    setIsImageQualityValid(valid);

    if (!valid) {
      globalErrorsStore.addErrorIfNotExists(featureTranslationKeys.errors.invalidQuality);
      console.error("[ImageQualityControl] Invalid image quality: ", dropdownChangeEvent.value);
    } else {
      onChange(dropdownChangeEvent.value);
      globalErrorsStore.removeError(featureTranslationKeys.errors.invalidQuality);
    }
  };

  return (
    <div className="image-quality-dropdown-with-icon">
      <TooltipWrapper
        targetRef=".image-quality"
        position="top"
        content={t(
          isImageQualityValid
            ? imageQualityControlTranslationKeys.imageQualityTooltip
            : featureTranslationKeys.errors.invalidQuality,
        )}
        disabled={disabled}
      />

      <span className="left-icon" aria-hidden="true">
        <i className="pi pi-image" />
      </span>

      <Dropdown
        invalid={!isImageQualityValid}
        className={classNames("image-quality", { error: !isImageQualityValid })}
        data-testid="image-quality-dropdown"
        value={imageQuality}
        options={imageQualityOptions}
        optionLabel="label"
        optionValue="value"
        onChange={handleImageQualityChange}
        placeholder={t(imageQualityControlTranslationKeys.imageQualityAutoLabel)}
        disabled={disabled}
        aria-label={t(imageQualityControlTranslationKeys.imageQualityTooltip)}
      />
    </div>
  );
};
